import React from 'react';
import { M } from '@dashboard-experience/mastodon';
import { useMastodonReport } from './MastodonReportContext';

// ECDEX-3740: report tables that render as a Mastodon table when
// `cdx_cp_mastodon_report` is on, and as the Bootstrap markup they have always
// used when it is off. A table is converted by swapping its tags for these, so
// both states stay in step until the flag is removed (then this file goes, and
// the callers use `M.Table` and friends directly).

type Variant = 'bordered' | 'summary';

interface ReportTableProps {
  children: React.ReactNode;
  // Flag off: `cp-table-sm` and the `cp-table-responsive` wrapper. Mastodon: the
  // small table, in a container that scrolls sideways.
  small?: boolean;
  variant?: Variant;
}

interface ClassNameProps {
  children?: React.ReactNode;
  className?: string;
}

interface RowHeaderProps extends ClassNameProps {
  scope?: 'row' | 'col';
}

export const ReportTable = ({
  children,
  small = false,
  variant = 'bordered',
}: ReportTableProps) => {
  const mastodon = useMastodonReport();

  if (mastodon) {
    return (
      <M.TableContainer
        className={[
          'report-table',
          small && 'report-table--scroll',
          variant === 'summary' && 'report-table--summary',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <M.Table size={small ? 'sm' : undefined}>{children}</M.Table>
      </M.TableContainer>
    );
  }

  const table = (
    <table
      className={[
        'cp-table',
        small && 'cp-table-sm',
        variant === 'summary' ? 'table-summary' : 'table-bottom-bordered',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </table>
  );

  return small ? <div className='cp-table-responsive'>{table}</div> : table;
};

export const ReportTableHead = ({ children }: ClassNameProps) =>
  useMastodonReport() ? (
    <M.TableHead>{children}</M.TableHead>
  ) : (
    <thead>{children}</thead>
  );

export const ReportTableBody = ({ children }: ClassNameProps) =>
  useMastodonReport() ? (
    <M.TableBody aria-live='off'>{children}</M.TableBody>
  ) : (
    <tbody>{children}</tbody>
  );

export const ReportTableRow = ({
  children,
  ...props
}: ClassNameProps & React.HTMLAttributes<HTMLTableRowElement>) =>
  useMastodonReport() ? (
    <M.TableRow {...props}>{children}</M.TableRow>
  ) : (
    <tr {...props}>{children}</tr>
  );

export const ReportTableCell = ({
  children,
  ...props
}: ClassNameProps & React.TdHTMLAttributes<HTMLTableCellElement>) =>
  useMastodonReport() ? (
    <M.TableCell {...props}>{children}</M.TableCell>
  ) : (
    <td {...props}>{children}</td>
  );

// A row label. Stays a real `th` in both states, so it keeps its role.
export const ReportTableRowHeader = ({
  children,
  scope = 'row',
  className,
}: RowHeaderProps) =>
  useMastodonReport() ? (
    <M.TableHeader scope={scope} className={className}>
      {children}
    </M.TableHeader>
  ) : (
    <th scope={scope} className={className}>
      {children}
    </th>
  );

// A row label that is a plain cell today; a row header with the flag on.
export const ReportTableRowLabel = ({
  children,
  ...props
}: ClassNameProps & React.TdHTMLAttributes<HTMLTableCellElement>) =>
  useMastodonReport() ? (
    <M.TableHeader scope='row' {...props}>
      {children}
    </M.TableHeader>
  ) : (
    <td {...props}>{children}</td>
  );

// A column title in a `thead`. Today's markup is a `td`, so it stays one with
// the flag off; with it on it is a Mastodon header cell scoped to its column,
// so a screen reader announces it as a column header.
export const ReportTableColumnHeader = ({
  children,
  ...props
}: ClassNameProps & React.TdHTMLAttributes<HTMLTableCellElement>) =>
  useMastodonReport() ? (
    <M.TableHeader scope='col' {...props}>
      {children}
    </M.TableHeader>
  ) : (
    <td {...props}>{children}</td>
  );

// A heading written as a bare `th` (no `scope`), as the PLV summary has. With
// the flag on it is a Mastodon header cell, which Carbon scopes to its column.
export const ReportTableThHeader = ({
  children,
  ...props
}: ClassNameProps & React.ThHTMLAttributes<HTMLTableCellElement>) =>
  useMastodonReport() ? (
    <M.TableHeader {...props}>{children}</M.TableHeader>
  ) : (
    <th {...props}>{children}</th>
  );

// The empty top-left cell of a table whose head is written as bare `th`s. It
// heads nothing, so with the flag on it is a plain cell.
export const ReportTableCornerCell = () =>
  useMastodonReport() ? <M.TableCell /> : <th />;
